import React from "react";
import {
  AlertTriangle,
  Building2,
  CalendarClock,
  Clock3,
  FileText,
  Loader2,
  Paperclip,
  Save,
  UserRound,
  X,
} from "lucide-react";

export type OrigemSolicitacao =
  | "cliente"
  | "portal_cliente"
  | "email_cliente"
  | "fiscalizacao"
  | "contador"
  | "interno"
  | "outro";

export type ProvidenciaStatus =
  | "nao_aplicavel"
  | "nao_iniciado"
  | "sem_agendamento"
  | "agendada"
  | "vistoria_tecnica_agendada"
  | "comparecimento_presencial"
  | "aguardando_documentos"
  | "aguardando_cliente"
  | "em_emissao"
  | "em_renovacao"
  | "emitido"
  | "concluida";

export type ProvidenciaTipoAtendimento =
  | "nao_definido"
  | "online"
  | "email"
  | "presencial"
  | "vistoria_tecnica"
  | "contador"
  | "telefone"
  | "outro";

export type TipoValidade =
  | "com_vencimento"
  | "sem_vencimento"
  | "sob_demanda"
  | "revisao_periodica";

export type Periodicidade =
  | "semanal"
  | "mensal"
  | "trimestral"
  | "semestral"
  | "anual"
  | "sem_validade"
  | "sob_demanda";

export type RegisterDocumentForm = {
  nome: string;
  categoria: string;
  orgaoEmissor: string;
  descricao: string;

  empresaAindaNaoPossuiDocumento: boolean;
  origemSolicitacao: OrigemSolicitacao;
  clienteSolicitante: string;
  motivoExigencia: string;

  tipoValidade: TipoValidade;
  periodicidade: Periodicidade;

  dataEmissao: string;
  dataValidade: string;

  areaResponsavel: string;
  responsavelNome: string;

  providenciaStatus: ProvidenciaStatus;
  providenciaAgendadaPara: string;
  providenciaTipoAtendimento: ProvidenciaTipoAtendimento;
  providenciaLocal: string;
  providenciaDocumentosNecessarios: string;
  providenciaObservacao: string;

  uploadObservacao: string;
  file: File | null;
};

const HEADER_HEIGHT = 52;
const LEFTBAR_WIDTH = 64;
const MOBILE_BREAKPOINT = 900;

export const CATEGORIA_OPTIONS: Array<{ value: string; label: string }> = [
  { value: "trabalhista", label: "Trabalhista" },
  { value: "fiscal", label: "Fiscal" },
  { value: "societario", label: "Societário" },
  { value: "contabil", label: "Contábil" },
  { value: "previdenciario", label: "Previdenciário" },
  { value: "seguranca_trabalho", label: "Segurança do Trabalho" },
  { value: "saude_ocupacional", label: "Saúde Ocupacional" },
  { value: "ambiental", label: "Ambiental" },
  { value: "juridico", label: "Jurídico" },
  { value: "operacional", label: "Operacional" },
  { value: "qualidade", label: "Qualidade" },
  { value: "compliance", label: "Compliance" },
  { value: "cliente_portal", label: "Cliente / Portal" },
  { value: "outros", label: "Outros" },
];

export const ORGAO_EMISSOR_OPTIONS: Array<{ value: string; label: string }> = [
  { value: "caixa_economica_federal", label: "Caixa Econômica Federal" },
  { value: "receita_federal", label: "Receita Federal" },
  { value: "procuradoria_geral_fazenda_nacional", label: "PGFN" },
  {
    value: "ministerio_trabalho_emprego",
    label: "Ministério do Trabalho e Emprego",
  },
  { value: "inss", label: "INSS" },
  { value: "junta_comercial", label: "Junta Comercial" },
  { value: "prefeitura_municipal", label: "Prefeitura Municipal" },
  {
    value: "secretaria_fazenda_estadual",
    label: "Secretaria de Fazenda Estadual",
  },
  {
    value: "secretaria_fazenda_municipal",
    label: "Secretaria de Fazenda Municipal",
  },
  { value: "corpo_bombeiros", label: "Corpo de Bombeiros" },
  { value: "conselho_classe", label: "Conselho de Classe" },
  { value: "crt_rj", label: "CRT-RJ" },
  { value: "crea", label: "CREA" },
  { value: "ibama", label: "IBAMA" },
  { value: "inea", label: "INEA" },
  { value: "contador", label: "Contador / Contabilidade" },
  { value: "cliente", label: "Cliente" },
  { value: "portal_cliente", label: "Portal do Cliente" },
  { value: "interno", label: "Interno" },
  { value: "outros", label: "Outros" },
];

function normalizeText(value: unknown) {
  return String(value ?? "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .toLowerCase();
}

function normalizeSelectValue(
  value: string,
  options: Array<{ value: string; label: string }>
) {
  const raw = String(value ?? "").trim();
  if (!raw) return "";

  const byValue = options.find((option) => option.value === raw);
  if (byValue) return byValue.value;

  const normalizedRaw = normalizeText(raw);

  const byLabel = options.find((option) => {
    return normalizeText(option.label) === normalizedRaw;
  });

  if (byLabel) return byLabel.value;

  return "";
}

function shouldShowProvidenciaDate(status: ProvidenciaStatus) {
  return (
    status === "agendada" ||
    status === "vistoria_tecnica_agendada" ||
    status === "comparecimento_presencial" ||
    status === "aguardando_documentos" ||
    status === "aguardando_cliente" ||
    status === "em_emissao" ||
    status === "em_renovacao"
  );
}

function shouldRequireValidity(
  documentoPossuido: boolean,
  tipoValidade: TipoValidade
) {
  return (
    documentoPossuido &&
    (tipoValidade === "com_vencimento" ||
      tipoValidade === "revisao_periodica")
  );
}

function useViewportWidth() {
  const [width, setWidth] = React.useState<number>(() =>
    typeof window === "undefined" ? 1280 : window.innerWidth
  );

  React.useEffect(() => {
    if (typeof window === "undefined") return;

    const update = () => {
      setWidth(window.innerWidth);
    };

    update();

    window.addEventListener("resize", update);

    return () => {
      window.removeEventListener("resize", update);
    };
  }, []);

  return width;
}

const RegisterDocumentSidePanel: React.FC<{
  open: boolean;
  departmentColor: string;
  form: RegisterDocumentForm;
  saving: boolean;
  error: string | null;
  onClose: () => void;
  onChange: <K extends keyof RegisterDocumentForm>(
    key: K,
    value: RegisterDocumentForm[K]
  ) => void;
  onSubmit: () => void;
}> = ({
  open,
  departmentColor,
  form,
  saving,
  error,
  onClose,
  onChange,
  onSubmit,
}) => {
  const viewportWidth = useViewportWidth();
  const isMobileViewport = viewportWidth < MOBILE_BREAKPOINT;

  React.useEffect(() => {
    if (!open) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape" && !saving) {
        onClose();
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [open, saving, onClose]);

  if (!open) return null;

  const documentoPossuido = !form.empresaAindaNaoPossuiDocumento;

  const nomeValue = String(form.nome ?? "");
  const categoriaValue = normalizeSelectValue(
    form.categoria,
    CATEGORIA_OPTIONS
  );
  const orgaoEmissorValue = normalizeSelectValue(
    form.orgaoEmissor,
    ORGAO_EMISSOR_OPTIONS
  );

  const dataValidadeValue = String(form.dataValidade ?? "");

  const validadeObrigatoria = shouldRequireValidity(
    documentoPossuido,
    form.tipoValidade
  );

  const hasRequiredBase =
    nomeValue.trim().length > 0 && categoriaValue.trim().length > 0;

  const hasRequiredValidity =
    !validadeObrigatoria || dataValidadeValue.trim().length > 0;

  const canSubmit = hasRequiredBase && hasRequiredValidity && !saving;

  const providenciaTitle = documentoPossuido
    ? "Controle de renovação / providência"
    : "Controle de emissão / providência inicial";

  const providenciaStatusLabel = documentoPossuido
    ? "Status da renovação"
    : "Status da emissão";

  const providenciaDateLabel = documentoPossuido
    ? "Data agendada da renovação"
    : form.providenciaStatus === "vistoria_tecnica_agendada"
    ? "Data agendada da vistoria técnica"
    : "Data agendada da emissão/providência";

  const showProvidenciaDate = shouldShowProvidenciaDate(form.providenciaStatus);

  const overlayBackdropStyle: React.CSSProperties = {
    ...sidePanelStyles.overlayBackdrop,
    top: isMobileViewport ? 0 : HEADER_HEIGHT,
    left: isMobileViewport ? 0 : LEFTBAR_WIDTH,
    zIndex: isMobileViewport ? 1400 : 1190,
  };

  const overlayPanelStyle: React.CSSProperties = {
    ...sidePanelStyles.overlayPanel,
    width: isMobileViewport
      ? "100vw"
      : `min(980px, calc(100vw - ${LEFTBAR_WIDTH}px))`,
    height: isMobileViewport ? "100vh" : `calc(100vh - ${HEADER_HEIGHT}px)`,
  };

  const handleEmpresaAindaNaoPossuiDocumentoChange = (checked: boolean) => {
    onChange("empresaAindaNaoPossuiDocumento", checked);

    if (checked) {
      onChange("tipoValidade", "sob_demanda");
      onChange("periodicidade", "sob_demanda");
      onChange("dataEmissao", "");
      onChange("dataValidade", "");
      onChange("uploadObservacao", "");
      onChange("file", null);

      onChange("providenciaStatus", "nao_iniciado");
      onChange("providenciaAgendadaPara", "");
      onChange("providenciaTipoAtendimento", "nao_definido");
      return;
    }

    onChange("tipoValidade", "com_vencimento");
    onChange("periodicidade", "sob_demanda");
    onChange("providenciaStatus", "sem_agendamento");
    onChange("providenciaAgendadaPara", "");
    onChange("providenciaTipoAtendimento", "nao_definido");
  };

  const handleTipoValidadeChange = (tipoValidade: TipoValidade) => {
    onChange("tipoValidade", tipoValidade);

    if (tipoValidade === "sem_vencimento") {
      onChange("periodicidade", "sem_validade");
      onChange("dataValidade", "");
      return;
    }

    if (tipoValidade === "sob_demanda") {
      onChange("periodicidade", "sob_demanda");
      onChange("dataValidade", "");
      return;
    }

    if (
      tipoValidade === "com_vencimento" &&
      form.periodicidade === "sem_validade"
    ) {
      onChange("periodicidade", "sob_demanda");
    }
  };

  const handleProvidenciaStatusChange = (status: ProvidenciaStatus) => {
    onChange("providenciaStatus", status);

    if (!shouldShowProvidenciaDate(status)) {
      onChange("providenciaAgendadaPara", "");
    }

    if (status === "vistoria_tecnica_agendada") {
      onChange("providenciaTipoAtendimento", "vistoria_tecnica");
    }

    if (status === "nao_aplicavel") {
      onChange("providenciaAgendadaPara", "");
      onChange("providenciaTipoAtendimento", "nao_definido");
    }
  };

  return (
    <div
      style={overlayBackdropStyle}
      onMouseDown={(event) => {
        if (event.target === event.currentTarget && !saving) {
          onClose();
        }
      }}
    >
      <div
        style={overlayPanelStyle}
        onMouseDown={(event) => event.stopPropagation()}
      >
        <div style={sidePanelStyles.overlayHeader}>
          <div>
            <div style={sidePanelStyles.overlayEyebrow}>
              Governança documental
            </div>
            <h2 style={sidePanelStyles.overlayTitle}>Registrar documento</h2>
            <p style={sidePanelStyles.overlaySubtitle}>
              Informe se a empresa já possui o documento ou se ele ainda precisa
              ser emitido. Quando ainda não possui, o registro será tratado como
              providência inicial, sem exigir validade ou anexo.
            </p>
          </div>

          <button
            type="button"
            style={sidePanelStyles.overlayCloseButton}
            onClick={onClose}
            disabled={saving}
          >
            <X size={17} />
          </button>
        </div>

        {error && (
          <div style={sidePanelStyles.overlayError}>
            <AlertTriangle size={15} />
            <span>{error}</span>
          </div>
        )}

        <div style={sidePanelStyles.formGrid}>
          <div style={sidePanelStyles.formFieldFull}>
            <div style={sidePanelStyles.formDivider}>
              <FileText size={14} />
              <span>Identificação do documento</span>
            </div>
          </div>

          <div style={sidePanelStyles.formFieldWide}>
            <label style={sidePanelStyles.formLabel}>Documento *</label>
            <input
              style={sidePanelStyles.formInput}
              value={form.nome}
              onChange={(event) => onChange("nome", event.target.value)}
              placeholder="Ex.: Certificado de Regularidade do FGTS"
              disabled={saving}
            />
          </div>

          <div style={sidePanelStyles.formField}>
            <label style={sidePanelStyles.formLabel}>Categoria *</label>
            <select
              style={sidePanelStyles.formInput}
              value={categoriaValue}
              onChange={(event) => onChange("categoria", event.target.value)}
              disabled={saving}
            >
              <option value="">Selecione a categoria</option>
              {CATEGORIA_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          <div style={sidePanelStyles.formField}>
            <label style={sidePanelStyles.formLabel}>Órgão emissor</label>
            <select
              style={sidePanelStyles.formInput}
              value={orgaoEmissorValue}
              onChange={(event) => onChange("orgaoEmissor", event.target.value)}
              disabled={saving}
            >
              <option value="">Selecione o órgão emissor</option>
              {ORGAO_EMISSOR_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          <div style={sidePanelStyles.formFieldFull}>
            <label style={sidePanelStyles.checkboxLine}>
              <input
                type="checkbox"
                checked={form.empresaAindaNaoPossuiDocumento}
                onChange={(event) =>
                  handleEmpresaAindaNaoPossuiDocumentoChange(
                    event.target.checked
                  )
                }
                disabled={saving}
              />
              <span>A empresa ainda não possui este documento</span>
            </label>

            <div style={sidePanelStyles.formHelpText}>
              Quando marcado, a API receberá{" "}
              <strong>documento_possuido = false</strong> pelo fluxo do
              formulário principal e a providência será tratada como emissão
              inicial.
            </div>
          </div>

          <div style={sidePanelStyles.formFieldFull}>
            <div style={sidePanelStyles.formDivider}>
              <Building2 size={14} />
              <span>Origem da exigência</span>
            </div>
          </div>

          <div style={sidePanelStyles.formField}>
            <label style={sidePanelStyles.formLabel}>
              Origem da solicitação
            </label>
            <select
              style={sidePanelStyles.formInput}
              value={form.origemSolicitacao}
              onChange={(event) =>
                onChange(
                  "origemSolicitacao",
                  event.target.value as OrigemSolicitacao
                )
              }
              disabled={saving}
            >
              <option value="cliente">Cliente</option>
              <option value="portal_cliente">Portal do cliente</option>
              <option value="email_cliente">E-mail do cliente</option>
              <option value="fiscalizacao">Fiscalização</option>
              <option value="contador">Contador</option>
              <option value="interno">Interno</option>
              <option value="outro">Outro</option>
            </select>
          </div>

          <div style={sidePanelStyles.formField}>
            <label style={sidePanelStyles.formLabel}>
              Cliente / solicitante
            </label>
            <input
              style={sidePanelStyles.formInput}
              value={form.clienteSolicitante}
              onChange={(event) =>
                onChange("clienteSolicitante", event.target.value)
              }
              placeholder="Ex.: Marlin Navegação / Lecsor / Cliente X"
              disabled={saving}
            />
          </div>

          <div style={sidePanelStyles.formFieldFull}>
            <label style={sidePanelStyles.formLabel}>Motivo da exigência</label>
            <textarea
              style={sidePanelStyles.formTextareaSmall}
              value={form.motivoExigencia}
              onChange={(event) =>
                onChange("motivoExigencia", event.target.value)
              }
              placeholder="Ex.: Cliente solicitou para habilitação cadastral, renovação de portal, liberação de pagamento ou qualificação como fornecedor."
              disabled={saving}
            />
          </div>

          {documentoPossuido && (
            <>
              <div style={sidePanelStyles.formFieldFull}>
                <div style={sidePanelStyles.formDivider}>
                  <CalendarClock size={14} />
                  <span>Validade do documento existente</span>
                </div>
              </div>

              <div style={sidePanelStyles.formField}>
                <label style={sidePanelStyles.formLabel}>
                  Tipo de validade
                </label>
                <select
                  style={sidePanelStyles.formInput}
                  value={form.tipoValidade}
                  onChange={(event) =>
                    handleTipoValidadeChange(
                      event.target.value as TipoValidade
                    )
                  }
                  disabled={saving}
                >
                  <option value="com_vencimento">Com vencimento</option>
                  <option value="sem_vencimento">Sem vencimento</option>
                  <option value="sob_demanda">Sob demanda</option>
                  <option value="revisao_periodica">Revisão periódica</option>
                </select>
              </div>

              <div style={sidePanelStyles.formField}>
                <label style={sidePanelStyles.formLabel}>Periodicidade</label>
                <select
                  style={sidePanelStyles.formInput}
                  value={form.periodicidade}
                  onChange={(event) =>
                    onChange(
                      "periodicidade",
                      event.target.value as Periodicidade
                    )
                  }
                  disabled={
                    saving ||
                    form.tipoValidade === "sem_vencimento" ||
                    form.tipoValidade === "sob_demanda"
                  }
                >
                  <option value="sob_demanda">Sob demanda</option>
                  <option value="semanal">Semanal</option>
                  <option value="mensal">Mensal</option>
                  <option value="trimestral">Trimestral</option>
                  <option value="semestral">Semestral</option>
                  <option value="anual">Anual</option>
                  <option value="sem_validade">Sem validade</option>
                </select>
              </div>

              <div style={sidePanelStyles.formField}>
                <label style={sidePanelStyles.formLabel}>Data de emissão</label>
                <input
                  type="date"
                  style={sidePanelStyles.formInput}
                  value={form.dataEmissao}
                  onChange={(event) =>
                    onChange("dataEmissao", event.target.value)
                  }
                  disabled={saving}
                />
              </div>

              <div style={sidePanelStyles.formField}>
                <label style={sidePanelStyles.formLabel}>
                  Data de vencimento do documento{" "}
                  {validadeObrigatoria ? "*" : ""}
                </label>
                <input
                  type="date"
                  style={sidePanelStyles.formInput}
                  value={form.dataValidade}
                  onChange={(event) =>
                    onChange("dataValidade", event.target.value)
                  }
                  disabled={
                    saving ||
                    form.tipoValidade === "sem_vencimento" ||
                    form.tipoValidade === "sob_demanda"
                  }
                />

                {validadeObrigatoria && !form.dataValidade && (
                  <div style={sidePanelStyles.formWarningText}>
                    Obrigatório para documentos com vencimento ou revisão
                    periódica.
                  </div>
                )}
              </div>

              <div style={sidePanelStyles.formFieldFull}>
                <div style={sidePanelStyles.formDivider}>
                  <Paperclip size={14} />
                  <span>Anexo vigente</span>
                </div>
              </div>

              <div style={sidePanelStyles.formFieldFull}>
                <label style={sidePanelStyles.uploadBox}>
                  <div style={sidePanelStyles.uploadBoxIcon}>
                    <Paperclip size={17} />
                  </div>

                  <div style={sidePanelStyles.uploadBoxText}>
                    <strong>
                      {form.file ? form.file.name : "Selecionar documento"}
                    </strong>
                    <span>
                      Opcional. Use para anexar o arquivo vigente já existente.
                      PDF, PNG, JPG ou JPEG.
                    </span>
                  </div>

                  <input
                    type="file"
                    accept=".pdf,.png,.jpg,.jpeg"
                    style={sidePanelStyles.hiddenFileInput}
                    disabled={saving}
                    onChange={(event) => {
                      const file = event.target.files?.[0] || null;
                      onChange("file", file);
                    }}
                  />
                </label>
              </div>

              <div style={sidePanelStyles.formFieldFull}>
                <label style={sidePanelStyles.formLabel}>
                  Observação do anexo
                </label>
                <textarea
                  style={sidePanelStyles.formTextareaSmall}
                  value={form.uploadObservacao}
                  onChange={(event) =>
                    onChange("uploadObservacao", event.target.value)
                  }
                  placeholder="Ex.: Documento emitido no portal oficial e anexado como versão vigente."
                  disabled={saving || !form.file}
                />
              </div>
            </>
          )}

          <div style={sidePanelStyles.formFieldFull}>
            <div style={sidePanelStyles.formDivider}>
              <Clock3 size={14} />
              <span>{providenciaTitle}</span>
            </div>
          </div>

          <div style={sidePanelStyles.formField}>
            <label style={sidePanelStyles.formLabel}>
              {providenciaStatusLabel}
            </label>
            <select
              style={sidePanelStyles.formInput}
              value={form.providenciaStatus}
              onChange={(event) =>
                handleProvidenciaStatusChange(
                  event.target.value as ProvidenciaStatus
                )
              }
              disabled={saving}
            >
              {documentoPossuido ? (
                <>
                  <option value="sem_agendamento">Sem agendamento</option>
                  <option value="agendada">Agendada</option>
                  <option value="vistoria_tecnica_agendada">
                    Vistoria técnica agendada
                  </option>
                  <option value="comparecimento_presencial">
                    Comparecimento presencial
                  </option>
                  <option value="aguardando_documentos">
                    Aguardando documentos
                  </option>
                  <option value="aguardando_cliente">Aguardando cliente</option>
                  <option value="em_renovacao">Em renovação</option>
                  <option value="concluida">Concluída</option>
                  <option value="nao_aplicavel">Não aplicável</option>
                </>
              ) : (
                <>
                  <option value="nao_iniciado">Não iniciado</option>
                  <option value="vistoria_tecnica_agendada">
                    Vistoria técnica agendada
                  </option>
                  <option value="agendada">Emissão agendada</option>
                  <option value="comparecimento_presencial">
                    Comparecimento presencial
                  </option>
                  <option value="aguardando_documentos">
                    Aguardando documentos
                  </option>
                  <option value="aguardando_cliente">Aguardando cliente</option>
                  <option value="em_emissao">Em emissão</option>
                  <option value="emitido">Emitido</option>
                </>
              )}
            </select>
          </div>

          {showProvidenciaDate && (
            <div style={sidePanelStyles.formField}>
              <label style={sidePanelStyles.formLabel}>
                {providenciaDateLabel}
              </label>
              <input
                type="datetime-local"
                style={sidePanelStyles.formInput}
                value={form.providenciaAgendadaPara}
                onChange={(event) =>
                  onChange("providenciaAgendadaPara", event.target.value)
                }
                disabled={saving}
              />
            </div>
          )}

          <div style={sidePanelStyles.formField}>
            <label style={sidePanelStyles.formLabel}>Tipo de atendimento</label>
            <select
              style={sidePanelStyles.formInput}
              value={form.providenciaTipoAtendimento}
              onChange={(event) =>
                onChange(
                  "providenciaTipoAtendimento",
                  event.target.value as ProvidenciaTipoAtendimento
                )
              }
              disabled={saving}
            >
              <option value="nao_definido">Não definido</option>
              <option value="online">Online</option>
              <option value="email">E-mail</option>
              <option value="presencial">Presencial</option>
              <option value="vistoria_tecnica">Vistoria técnica</option>
              <option value="contador">Contador</option>
              <option value="telefone">Telefone</option>
              <option value="outro">Outro</option>
            </select>
          </div>

          <div style={sidePanelStyles.formField}>
            <label style={sidePanelStyles.formLabel}>
              Local / portal / órgão
            </label>
            <input
              style={sidePanelStyles.formInput}
              value={form.providenciaLocal}
              onChange={(event) =>
                onChange("providenciaLocal", event.target.value)
              }
              placeholder="Ex.: Prefeitura, Receita, Junta Comercial, portal do cliente..."
              disabled={saving}
            />
          </div>

          <div style={sidePanelStyles.formFieldFull}>
            <label style={sidePanelStyles.formLabel}>
              Documentos necessários para emissão ou renovação
            </label>
            <textarea
              style={sidePanelStyles.formTextarea}
              value={form.providenciaDocumentosNecessarios}
              onChange={(event) =>
                onChange("providenciaDocumentosNecessarios", event.target.value)
              }
              placeholder="Ex.: Contrato social, cartão CNPJ, procuração, RG/CPF do sócio, comprovante de endereço, taxa paga, certificado digital, documentos do contador..."
              disabled={saving}
            />
          </div>

          <div style={sidePanelStyles.formFieldFull}>
            <label style={sidePanelStyles.formLabel}>
              Observação da providência
            </label>
            <textarea
              style={sidePanelStyles.formTextareaSmall}
              value={form.providenciaObservacao}
              onChange={(event) =>
                onChange("providenciaObservacao", event.target.value)
              }
              placeholder="Ex.: Comparecer ao local com documentos originais; aguardar vistoria técnica; ligar antes; aguardar retorno do cliente; emitir pelo portal oficial."
              disabled={saving}
            />
          </div>

          <div style={sidePanelStyles.formFieldFull}>
            <div style={sidePanelStyles.formDivider}>
              <UserRound size={14} />
              <span>Responsabilidade interna</span>
            </div>
          </div>

          <div style={sidePanelStyles.formField}>
            <label style={sidePanelStyles.formLabel}>Área responsável</label>
            <input
              style={sidePanelStyles.formInput}
              value={form.areaResponsavel}
              onChange={(event) =>
                onChange("areaResponsavel", event.target.value)
              }
              placeholder="Ex.: Departamento Pessoal"
              disabled={saving}
            />
          </div>

          <div style={sidePanelStyles.formField}>
            <label style={sidePanelStyles.formLabel}>Responsável</label>
            <input
              style={sidePanelStyles.formInput}
              value={form.responsavelNome}
              onChange={(event) =>
                onChange("responsavelNome", event.target.value)
              }
              placeholder="Ex.: DP / Contabilidade / Guilherme"
              disabled={saving}
            />
          </div>

          <div style={sidePanelStyles.formFieldFull}>
            <label style={sidePanelStyles.formLabel}>
              Descrição / observação geral
            </label>
            <textarea
              style={sidePanelStyles.formTextarea}
              value={form.descricao}
              onChange={(event) => onChange("descricao", event.target.value)}
              placeholder="Descrição breve sobre finalidade, exigência, regra de controle ou contexto deste documento."
              disabled={saving}
            />
          </div>
        </div>

        <div style={sidePanelStyles.overlayFooter}>
          <button
            type="button"
            style={sidePanelStyles.cancelButton}
            onClick={onClose}
            disabled={saving}
          >
            Cancelar
          </button>

          <button
            type="button"
            style={{
              ...sidePanelStyles.saveButton,
              background: canSubmit ? departmentColor : "#94a3b8",
              borderColor: canSubmit ? departmentColor : "#94a3b8",
              cursor: canSubmit ? "pointer" : "not-allowed",
              opacity: canSubmit ? 1 : 0.82,
            }}
            onClick={onSubmit}
            disabled={!canSubmit}
          >
            {saving ? <Loader2 size={15} /> : <Save size={15} />}
            {documentoPossuido && form.file
              ? "Registrar e anexar"
              : "Registrar documento"}
          </button>
        </div>
      </div>
    </div>
  );
};

const sidePanelStyles: Record<string, React.CSSProperties> = {
  cancelButton: {
    height: 34,
    padding: "0 13px",
    border: "1px solid #cbd5e1",
    background: "#ffffff",
    color: "#334155",
    fontSize: 12,
    fontWeight: 850,
    cursor: "pointer",
    fontFamily: "inherit",
  },

  checkboxLine: {
    minHeight: 32,
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    color: "#111827",
    fontSize: 12.3,
    fontWeight: 850,
    cursor: "pointer",
  },

  formDivider: {
    marginTop: 4,
    paddingTop: 8,
    borderTop: "1px solid #eef2f7",
    display: "flex",
    alignItems: "center",
    gap: 7,
    color: "#111827",
    fontSize: 12.5,
    fontWeight: 950,
  },

  formField: {
    minWidth: 0,
  },

  formFieldFull: {
    gridColumn: "1 / -1",
    minWidth: 0,
  },

  formFieldWide: {
    gridColumn: "span 2",
    minWidth: 0,
  },

  formGrid: {
    flex: 1,
    overflowY: "auto",
    background: "linear-gradient(180deg, #f8fafc 0%, #f1f5f9 100%)",
    padding: 14,
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    gap: 10,
  },

  formHelpText: {
    marginTop: 3,
    color: "#64748b",
    fontSize: 11.4,
    fontWeight: 650,
  },

  formInput: {
    width: "100%",
    height: 34,
    border: "1px solid #cbd5e1",
    background: "#ffffff",
    color: "#111827",
    padding: "0 10px",
    fontSize: 12.2,
    fontWeight: 750,
    outline: "none",
    fontFamily: "inherit",
    boxSizing: "border-box",
  },

  formLabel: {
    display: "block",
    marginBottom: 5,
    color: "#475569",
    fontSize: 10.8,
    fontWeight: 900,
    textTransform: "uppercase",
    letterSpacing: 0.2,
  },

  formTextarea: {
    width: "100%",
    minHeight: 82,
    border: "1px solid #cbd5e1",
    background: "#ffffff",
    color: "#111827",
    padding: "9px 10px",
    fontSize: 12.2,
    fontWeight: 750,
    outline: "none",
    resize: "vertical",
    fontFamily: "inherit",
    boxSizing: "border-box",
  },

  formTextareaSmall: {
    width: "100%",
    minHeight: 58,
    border: "1px solid #cbd5e1",
    background: "#ffffff",
    color: "#111827",
    padding: "9px 10px",
    fontSize: 12.2,
    fontWeight: 750,
    outline: "none",
    resize: "vertical",
    fontFamily: "inherit",
    boxSizing: "border-box",
  },

  formWarningText: {
    marginTop: 5,
    color: "#92400e",
    fontSize: 11.2,
    fontWeight: 800,
  },

  hiddenFileInput: {
    display: "none",
  },

  overlayBackdrop: {
    position: "fixed",
    top: HEADER_HEIGHT,
    left: LEFTBAR_WIDTH,
    right: 0,
    bottom: 0,
    zIndex: 1190,
    display: "flex",
    justifyContent: "flex-end",
    background:
      "radial-gradient(circle at top left, rgba(47, 63, 89, 0.10), transparent 34%), rgba(15, 23, 42, 0.28)",
    backdropFilter: "blur(3px)",
    WebkitBackdropFilter: "blur(3px)",
    boxSizing: "border-box",
  },

  overlayCloseButton: {
    width: 32,
    height: 32,
    border: "1px solid #cbd5e1",
    background: "#ffffff",
    color: "#334155",
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },

  overlayError: {
    margin: "12px 18px 0",
    border: "1px solid #fecaca",
    background: "#fef2f2",
    color: "#991b1b",
    padding: "8px 10px",
    display: "flex",
    alignItems: "center",
    gap: 8,
    fontSize: 12,
    fontWeight: 800,
  },

  overlayEyebrow: {
    color: "#64748b",
    fontSize: 10.8,
    fontWeight: 950,
    textTransform: "uppercase",
    letterSpacing: 0.4,
    marginBottom: 4,
  },

  overlayFooter: {
    padding: "12px 18px 16px",
    borderTop: "1px solid #e5e7eb",
    background: "#f8fafc",
    display: "flex",
    justifyContent: "flex-end",
    gap: 8,
    flexShrink: 0,
  },

  overlayHeader: {
    padding: "16px 18px 14px",
    borderBottom: "1px solid #e5e7eb",
    background: "#ffffff",
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: 12,
    flexShrink: 0,
  },

  overlayPanel: {
    width: `min(980px, calc(100vw - ${LEFTBAR_WIDTH}px))`,
    height: `calc(100vh - ${HEADER_HEIGHT}px)`,
    background: "#ffffff",
    borderLeft: "1px solid #d9e2ec",
    boxShadow: "-18px 0 46px rgba(15, 23, 42, 0.22)",
    display: "flex",
    flexDirection: "column",
    color: "#0f172a",
    overflow: "hidden",
    boxSizing: "border-box",
  },

  overlaySubtitle: {
    margin: "5px 0 0",
    color: "#64748b",
    fontSize: 12.2,
    fontWeight: 700,
    lineHeight: 1.35,
    maxWidth: 780,
  },

  overlayTitle: {
    margin: 0,
    color: "#111827",
    fontSize: 18,
    fontWeight: 950,
    lineHeight: 1.15,
  },

  saveButton: {
    height: 34,
    padding: "0 14px",
    border: "1px solid",
    color: "#ffffff",
    fontSize: 12,
    fontWeight: 900,
    cursor: "pointer",
    fontFamily: "inherit",
    display: "inline-flex",
    alignItems: "center",
    gap: 7,
  },

  uploadBox: {
    minHeight: 62,
    border: "1px dashed #94a3b8",
    background: "#f8fafc",
    padding: 12,
    display: "flex",
    alignItems: "center",
    gap: 10,
    cursor: "pointer",
  },

  uploadBoxIcon: {
    width: 34,
    height: 34,
    border: "1px solid #cbd5e1",
    background: "#ffffff",
    color: "#334155",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },

  uploadBoxText: {
    display: "flex",
    flexDirection: "column",
    gap: 2,
    color: "#334155",
    fontSize: 12,
    fontWeight: 700,
  },
};

export default RegisterDocumentSidePanel;